import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, CreditCard, AlertCircle } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending: "À régler",
  invoiced: "Facturée — en attente de règlement",
  paid: "Payée ✓",
  failed: "Paiement échoué — à régler",
  partial: "Règlement partiel",
};

export default function PublicInstallment() {
  const { pid, idx } = useParams();
  const [search] = useSearchParams();
  const sessionId = search.get("session_id");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [polling, setPolling] = useState(false);
  const [paidNow, setPaidNow] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API_BASE}/installments/public/${pid}/${idx}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Échéance introuvable");
    }
  };

  useEffect(() => { load(); }, [pid, idx]);

  // Poll after Stripe redirect
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const max = 12;
    setPolling(true);
    const tick = async () => {
      try {
        const r = await axios.get(`${API_BASE}/installments/checkout-status/${sessionId}`);
        if (cancelled) return;
        if (r.data?.payment_status === "paid") {
          setPaidNow(true);
          setPolling(false);
          await load();
          return;
        }
        attempts++;
        if (attempts < max) setTimeout(tick, 2000);
        else setPolling(false);
      } catch { setPolling(false); }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, pid, idx]);

  const pay = async () => {
    setPaying(true);
    try {
      const r = await axios.post(
        `${API_BASE}/installments/public/${pid}/${idx}/checkout`,
        { origin_url: window.location.origin },
      );
      window.location.href = r.data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur Stripe");
      setPaying(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-[#FAFAFA]">
        <div className="bg-white rounded-3xl border border-black/8 p-8 max-w-md text-center shadow-sm">
          <AlertCircle className="mx-auto text-red-500 mb-3" size={42} />
          <div className="display text-xl font-extrabold text-[#0A1128]">Lien invalide</div>
          <div className="text-sm text-[#64748B] mt-2">{error}</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <Loader2 className="animate-spin text-[#06B6D4]" size={40} />
      </div>
    );
  }

  const isPaid = data.installment.status === "paid" || paidNow;

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-12 px-4">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#06B6D4] font-extrabold">BESSON SOLUTIONS</div>
          <div className="display text-3xl font-extrabold text-[#0A1128] mt-1">Règlement d&apos;une échéance</div>
        </div>

        <div className="bg-white border border-black/8 rounded-3xl overflow-hidden shadow-sm" data-testid="public-installment-card">
          <div className="bg-[#0A1128] text-white px-6 py-5">
            <div className="text-[10px] uppercase tracking-widest text-[#67E8F9] font-bold">{data.project.order_ref || "Commande"}</div>
            <div className="display font-extrabold text-lg mt-1">{data.project.title}</div>
            <div className="text-xs text-white/70 mt-0.5">{data.client_name}</div>
          </div>

          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between bg-[#FAFAFA] border border-black/8 rounded-2xl px-4 py-4">
              <div>
                <div className="text-xs text-[#64748B]">Échéance #{data.installment.index}</div>
                <div className="font-bold text-[#0A1128]">{data.installment.label}</div>
                <div className="text-xs text-[#64748B] mt-0.5">À échoir le {data.installment.due_at}</div>
              </div>
              <div className="text-right">
                <div className="display text-3xl font-extrabold text-[#0A1128]">
                  {(parseFloat(data.installment.amount) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €
                </div>
                <div className="text-[10px] text-[#64748B] mt-0.5">TTC</div>
              </div>
            </div>

            {data.invoice?.ref && (
              <div className="text-xs text-[#64748B] flex items-center gap-2">
                Facture <span className="font-bold text-[#0A1128]">{data.invoice.ref}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isPaid ? "bg-[#A7F3D0]/40 text-[#065F46]" : "bg-[#06B6D4]/15 text-[#06B6D4]"}`}>
                  {STATUS_LABEL[isPaid ? "paid" : data.installment.status] || data.installment.status}
                </span>
              </div>
            )}

            {isPaid ? (
              <div className="bg-[#A7F3D0]/30 border border-[#A7F3D0] rounded-2xl p-5 text-center" data-testid="public-installment-paid">
                <CheckCircle2 className="mx-auto text-[#065F46] mb-2" size={36} />
                <div className="display font-extrabold text-[#0A1128]">Paiement enregistré ✓</div>
                <div className="text-xs text-[#64748B] mt-1">Merci ! Un email de confirmation vous a été envoyé.</div>
              </div>
            ) : polling ? (
              <div className="bg-[#06B6D4]/10 border border-[#06B6D4]/30 rounded-2xl p-5 text-center">
                <Loader2 className="mx-auto animate-spin text-[#06B6D4] mb-2" size={28} />
                <div className="text-sm font-bold text-[#0A1128]">Vérification du paiement…</div>
              </div>
            ) : (
              <button
                onClick={pay}
                disabled={paying}
                data-testid="public-installment-pay-btn"
                className="w-full bg-[#0A1128] hover:bg-[#14213d] text-white font-extrabold py-4 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
              >
                {paying ? <Loader2 className="animate-spin" size={18} /> : <CreditCard size={18} />}
                Régler par carte bancaire
              </button>
            )}

            <div className="text-[10px] text-center text-[#64748B] leading-relaxed">
              Paiement sécurisé via Stripe · BESSON SOLUTIONS · Aucune donnée bancaire stockée sur nos serveurs.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
